import { ArrowUp, CheckSquare, Square } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { View, XStack, YStack } from "tamagui";
import { PrimaryCheckText } from "../../core/components/PrimaryCheck";
import { getDayJs } from "../../core/dayJs";
import { useStores } from "../../core/stores";
import { ModuleBlock } from "../../ui/Block";
import { Body } from "../../ui/Body";
import { Box } from "../../ui/Box";
import type { InfoRowItemProps } from "../../ui/InfoRow";
import { Pill, PillType } from "../../ui/Pill";
import { ModuleSetupBase } from "../core/ModuleUIRenderer";
import type DateDueModuleModel from "./DateDueModuleModel";
import { DateDueUI } from "./DateDueUI";

export const DateDueModuleUI = observer(
  ({ module }: { module: DateDueModuleModel }) => {
    return (
      <View>
        {!module.data.title && (
          <Box>
            <XStack gap="$medium">
              <ArrowUp />
              <Body>Set your title above!</Body>
            </XStack>
          </Box>
        )}

        <DateDueUI module={module} />
      </View>
    );
  },
);

export const DateDueModuleBlockUI = observer(
  ({ module }: { module: DateDueModuleModel }) => {
    const { usersStore } = useStores();

    const snapshot = module.data.snapshot;
    const infoRowItems = React.useMemo<InfoRowItemProps[] | undefined>(() => {
      if (!snapshot.assignedTo?.length || !Array.isArray(snapshot.assignedTo)) {
        return;
      }

      const completedBy = snapshot.assignedTo?.filter((assignedToUserUid) => {
        return snapshot.completedBy?.includes(assignedToUserUid);
      });

      const notCompletedBy = snapshot.assignedTo?.filter(
        (assignedToUserUid) => {
          return !completedBy?.includes(assignedToUserUid);
        },
      );

      const items = [];

      if (completedBy.length) {
        items.push({
          icon: CheckSquare,
          text: completedBy
            .map((uid) => {
              return usersStore.byId.get(uid)?.data.displayName ?? "Unknown";
            })
            .join(", "),
        });
      }

      if (notCompletedBy.length) {
        items.push({
          icon: Square,
          text: notCompletedBy
            .map((uid) => {
              return usersStore.byId.get(uid)?.data.displayName ?? "Unknown";
            })
            .join(", "),
        });
      }

      return items;
    }, [snapshot, usersStore]);

    let content: JSX.Element;
    if (!snapshot.dateStart) {
      content = <Body>No upcoming event found</Body>;
    } else {
      const isOngoing =
        snapshot.dateStart &&
        snapshot.dateEnd &&
        Date.now() > snapshot.dateStart.toMillis() &&
        Date.now() < snapshot.dateEnd.toMillis();

      let isDue = false;
      if (snapshot.dateEnd) {
        isDue = Date.now() > snapshot.dateEnd.toMillis();
      } else if (snapshot.dateStart) {
        isDue = Date.now() > snapshot.dateStart.toMillis();
      }

      const isCompleted =
        (snapshot.completedBy &&
          snapshot.completedBy.length > 0 &&
          snapshot.assignedTo?.length === 0) ||
        ((snapshot.assignedTo?.length ?? 0) > 0 &&
          snapshot.completedBy?.length === snapshot.assignedTo?.length);

      let timeRow: JSX.Element;
      if (isCompleted) {
        timeRow = <PrimaryCheckText text={"Completed"} />;
      } else if (isDue) {
        timeRow = (
          <Body>Due {getDayJs(snapshot.dateEnd ?? snapshot.dateStart)}</Body>
        );
      } else if (isOngoing) {
        timeRow = (
          <Body>Due {getDayJs(snapshot.dateEnd ?? snapshot.dateStart)}</Body>
        );
      } else {
        timeRow = <Body>Starts {getDayJs(snapshot.dateStart)}</Body>;
      }

      const showPills = !isCompleted && (isOngoing || isDue);

      content = (
        <YStack gap="$medium">
          {showPills && (
            <XStack gap="$medium" alignItems="center">
              {isOngoing && <Pill type={PillType.Info}>ONGOING</Pill>}
              {isDue && <Pill type={PillType.Danger}>OVER DUE</Pill>}
            </XStack>
          )}
          {timeRow}
        </YStack>
      );
    }

    return (
      <ModuleBlock module={module} infoRowItems={infoRowItems}>
        {content}
      </ModuleBlock>
    );
  },
);

export const DateDueModuleSetupUI = observer(
  ({ module }: { module: DateDueModuleModel }) => {
    return (
      <YStack gap="$larger">
        <ModuleSetupBase
          module={module}
          title="Event"
          titlePlaceholder="Set title..."
          description="Start by adding an icon and title, you will add details in the next step"
        />
      </YStack>
    );
  },
);
